import { getTimeString } from '../../../../backend/utils/getTimeString';
import { ShiftDetailsAdminActions } from '../../../../ui/features/shift-details/components/shift-details-actions';
import { useMissionsTranslation } from '../../../../ui/hooks';
import { IconPlaceholder } from '@juvare/common-web-ui/IconPlaceholder';
import { themeColors } from '@juvare/common-web-ui/JuvareTheme';
import { NavigationContext } from '@juvare/common-web-ui/NavigationContext';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SupervisorAccountOutlinedIcon from '@mui/icons-material/SupervisorAccountOutlined';
import TimelapseIcon from '@mui/icons-material/Timelapse';
import { Box, Stack, Typography } from '@mui/material';
import React from 'react';
import { useNavigate } from 'react-router';
const DateAndTimeColumn = ({ row }) => {
    const { t } = useMissionsTranslation();
    return (React.createElement(Stack, { spacing: 1 },
        React.createElement(Typography, { variant: 'subtitle2' }, t('date', { value: row.startTime })),
        React.createElement(Typography, { variant: 'overline', sx: { color: themeColors.text.subtle } },
            t('time', { value: row.startTime }),
            " - ",
            t('time', { value: row.stopTime }))));
};
const CountyColumn = ({ row }) => {
    return React.createElement(Typography, { variant: 'subtitle2' }, row.county);
};
const VolunteersColumn = ({ row }) => {
    var _a, _b;
    const { t } = useMissionsTranslation();
    return (React.createElement(Box, { sx: { display: 'flex', alignItems: 'center', gap: '8px' } },
        React.createElement(IconPlaceholder, { status: ((_a = row.approvedParticipantsIds) === null || _a === void 0 ? void 0 : _a.length) ? 'primary' : 'disabled' },
            React.createElement(SupervisorAccountOutlinedIcon, null)),
        React.createElement(Typography, { variant: 'body2' }, t('timeTracking.volunteer', { count: (_b = row.approvedParticipantsIds) === null || _b === void 0 ? void 0 : _b.length }))));
};
const ApprovedColumn = ({ row }) => {
    var _a;
    const { t } = useMissionsTranslation();
    return (React.createElement(Box, { sx: { display: 'flex', alignItems: 'center', gap: '8px' } },
        React.createElement(IconPlaceholder, { status: row.approvedHours ? 'success' : 'disabled' },
            React.createElement(CheckCircleOutlineIcon, null)),
        React.createElement(Typography, { variant: 'body2' },
            React.createElement(Typography, { variant: 'subtitle2', component: 'span' }, getTimeString((_a = row.approvedHours) !== null && _a !== void 0 ? _a : 0, true)),
            t('timeTracking.approved'))));
};
const PendingColumn = ({ row }) => {
    var _a;
    const { t } = useMissionsTranslation();
    return (React.createElement(Box, { sx: { display: 'flex', alignItems: 'center', gap: '8px' } },
        React.createElement(IconPlaceholder, { status: row.pendingHours ? 'warning' : 'disabled' },
            React.createElement(TimelapseIcon, null)),
        React.createElement(Typography, { variant: 'body2' },
            React.createElement(Typography, { variant: 'subtitle2', component: 'span' }, getTimeString((_a = row.pendingHours) !== null && _a !== void 0 ? _a : 0, true)),
            t('timeTracking.pending'))));
};
const ActionsColumn = ({ row }) => {
    const navigate = useNavigate();
    const navigationContext = React.useContext(NavigationContext);
    const handleEditShift = (shiftId) => {
        navigationContext.navigate(() => {
            navigate(`edit-shift/${shiftId}`);
        });
    };
    const handleDuplicateShift = (shiftId) => {
        navigationContext.navigate(() => {
            navigate(`duplicate-shift/${shiftId}`);
        });
    };
    return (React.createElement(Box, { sx: { display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '8px', flexGrow: 1 } },
        React.createElement(ShiftDetailsAdminActions, { shiftId: row.id, onDuplicateShift: handleDuplicateShift, onEditShift: handleEditShift, missionStatus: row.missionStatus })));
};
export function useAdminPastShiftsColumns({ t, enableShiftActions = true }) {
    return React.useMemo(() => [
        {
            headerLabel: t('shifts.pastShiftsTable.dateAndTime'),
            cellFunction: (row) => React.createElement(DateAndTimeColumn, { row: row }),
            tableCellSx: {
                maxWidth: '200px',
            },
        },
        {
            headerLabel: t('shifts.pastShiftsTable.county'),
            cellFunction: (row) => React.createElement(CountyColumn, { row: row }),
            tableCellSx: {
                maxWidth: '200px',
            },
        },
        {
            headerLabel: t('shifts.pastShiftsTable.volunteers'),
            cellFunction: (row) => React.createElement(VolunteersColumn, { row: row }),
        },
        {
            headerLabel: t('shifts.pastShiftsTable.approvedHours'),
            cellFunction: (row) => React.createElement(ApprovedColumn, { row: row }),
        },
        {
            headerLabel: t('shifts.pastShiftsTable.pendingHours'),
            cellFunction: (row) => React.createElement(PendingColumn, { row: row }),
        },
        ...(enableShiftActions
            ? [
                {
                    headerLabel: t('shifts.pastShiftsTable.actions'),
                    cellFunction: (row) => React.createElement(ActionsColumn, { row: row }),
                },
            ]
            : []),
    ], [t, enableShiftActions]);
}
